import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../../services/api';
import styles from './BatchAllVolumesTool.module.css';

/* ────────── constants ────────── */
const EDGE_TTS_PREFIX = '/edge-tts';
const TRANSLATE_MODEL = 'gpt-5-nano';
const NOTES_MODEL = 'gpt-5-nano';
const TRANSLATE_CHUNK_SIZE = 20;
const TRANSLATE_CONCURRENCY = 100;
const POLL_MS = 3000;

const VOICE_CATEGORIES = [
  { value: '', label: '全部' },
  { value: 'zh', label: '中文' },
  { value: 'en', label: 'English' },
  { value: 'multilingual', label: '多语言' },
];

const STEP_KEYS = ['translate', 'notes', 'tts', 'combine'];
const STEP_LABELS = {
  translate: '翻译 (OpenAI)',
  notes: 'AI 笔记',
  tts: '语音生成 (Edge TTS)',
  combine: 'OSS 混音',
};

const initialStepState = () => ({
  status: 'idle', // idle | running | done | failed | skipped
  progress: 0,
  detail: '',
});

/* ────────── helpers ────────── */
function ts() {
  return new Date().toLocaleTimeString('zh-CN', { hour12: false });
}

function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

/* ────────── component ────────── */
function BatchAllVolumesTool() {
  /* ── volumes ── */
  const [allVolumes, setAllVolumes] = useState([]);
  const [volumesLoading, setVolumesLoading] = useState(false);
  const [orderedVolumeIds, setOrderedVolumeIds] = useState([]); // 排序后的 volume id 列表
  const [selectedIds, setSelectedIds] = useState(new Set());    // 勾选的 volume id

  /* ── voices ── */
  const [voices, setVoices] = useState([]);
  const [voicesLoading, setVoicesLoading] = useState(false);
  const [selectedEnVoice, setSelectedEnVoice] = useState(null);
  const [selectedCnVoice, setSelectedCnVoice] = useState(null);
  const [enVoiceFilter, setEnVoiceFilter] = useState('en');
  const [cnVoiceFilter, setCnVoiceFilter] = useState('zh');

  /* ── pipeline state ── */
  const [running, setRunning] = useState(false);
  const [currentVolIdx, setCurrentVolIdx] = useState(-1);     // 当前执行到第几个卷
  const [currentVolId, setCurrentVolId] = useState(null);
  const [steps, setSteps] = useState(() =>
    Object.fromEntries(STEP_KEYS.map((k) => [k, initialStepState()])),
  );
  const [volumeResults, setVolumeResults] = useState(new Map()); // volId -> 'done' | 'failed' | 'skipped'
  const [logs, setLogs] = useState([]);
  const [globalError, setGlobalError] = useState('');
  const cancelledRef = useRef(false);
  const logEndRef = useRef(null);

  /* ── drag state ── */
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);

  /* ── derived ── */
  const enFilteredVoices = useMemo(() => {
    if (!enVoiceFilter) return voices;
    return voices.filter((v) => v.category === enVoiceFilter);
  }, [voices, enVoiceFilter]);

  const cnFilteredVoices = useMemo(() => {
    if (!cnVoiceFilter) return voices;
    return voices.filter((v) => v.category === cnVoiceFilter);
  }, [voices, cnVoiceFilter]);

  const volumeMap = useMemo(() => {
    const m = new Map();
    for (const v of allVolumes) m.set(v.id, v);
    return m;
  }, [allVolumes]);

  const orderedVolumes = useMemo(() => {
    return orderedVolumeIds.map((id) => volumeMap.get(id)).filter(Boolean);
  }, [orderedVolumeIds, volumeMap]);

  const selectedVolumes = useMemo(() => {
    return orderedVolumes.filter((v) => selectedIds.has(v.id));
  }, [orderedVolumes, selectedIds]);

  const totalChapters = useMemo(() => {
    return selectedVolumes.reduce((sum, v) => sum + (v.chapters?.length || 0), 0);
  }, [selectedVolumes]);

  /* ── log helper ── */
  const addLog = useCallback((text, type = 'info') => {
    setLogs((prev) => [...prev, { time: ts(), text, type }]);
  }, []);

  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [logs]);

  /* ── load data ── */
  const loadVolumes = useCallback(async () => {
    setVolumesLoading(true);
    try {
      const { data } = await api.get('/volumes/', {
        params: { limit: 500, include_chapters: true },
      });
      const list = Array.isArray(data) ? data : [];
      setAllVolumes(list);
      setOrderedVolumeIds(list.map((v) => v.id));
      setSelectedIds(new Set(list.map((v) => v.id))); // 默认全选
    } catch {
      addLog('获取卷列表失败', 'error');
    } finally {
      setVolumesLoading(false);
    }
  }, [addLog]);

  const loadVoices = useCallback(async () => {
    setVoicesLoading(true);
    try {
      const { data } = await api.get(`${EDGE_TTS_PREFIX}/voices/all`);
      const list = Array.isArray(data?.voices) ? data.voices : [];
      setVoices(list);
      setSelectedEnVoice((prev) => {
        if (prev && prev.category !== 'zh' && list.some((v) => v.shortName === prev.shortName)) return prev;
        return list.find((v) => v.category === 'en') || list.find((v) => v.category === 'multilingual') || null;
      });
      setSelectedCnVoice((prev) => {
        if (prev && prev.category !== 'en' && list.some((v) => v.shortName === prev.shortName)) return prev;
        return list.find((v) => v.category === 'zh') || list.find((v) => v.category === 'multilingual') || null;
      });
    } catch {
      addLog('获取声音列表失败', 'error');
    } finally {
      setVoicesLoading(false);
    }
  }, [addLog]);

  useEffect(() => {
    loadVolumes();
    loadVoices();
  }, [loadVolumes, loadVoices]);

  /* ── step helpers ── */
  const updateStep = useCallback((key, patch) => {
    setSteps((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  }, []);

  const resetSteps = useCallback(() => {
    setSteps(Object.fromEntries(STEP_KEYS.map((k) => [k, initialStepState()])));
  }, []);

  /* ── sorting helpers ── */
  const moveVolume = useCallback((fromIdx, toIdx) => {
    setOrderedVolumeIds((prev) => {
      const next = [...prev];
      const [removed] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, removed);
      return next;
    });
  }, []);

  const handleDragStart = useCallback((idx) => {
    dragItem.current = idx;
  }, []);

  const handleDragEnter = useCallback((idx) => {
    dragOverItem.current = idx;
  }, []);

  const handleDragEnd = useCallback(() => {
    if (dragItem.current !== null && dragOverItem.current !== null && dragItem.current !== dragOverItem.current) {
      moveVolume(dragItem.current, dragOverItem.current);
    }
    dragItem.current = null;
    dragOverItem.current = null;
  }, [moveVolume]);

  const toggleSelect = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    if (selectedIds.size === orderedVolumeIds.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(orderedVolumeIds));
    }
  }, [selectedIds.size, orderedVolumeIds]);

  /* ────────── Pipeline sub-steps (same logic as BatchPipelineTool) ────────── */

  const pollTtsTask = useCallback(async (taskId) => {
    while (true) {
      if (cancelledRef.current) throw new Error('用户取消');
      await sleep(POLL_MS);
      const { data } = await api.get(`${EDGE_TTS_PREFIX}/tts/${taskId}`);
      if (data.status === 'completed') return data;
      if (data.status === 'failed') throw new Error(data.error || 'TTS 失败');
    }
  }, []);

  const fetchOssStatus = useCallback(async (chapterIdsList) => {
    const ossMap = new Map();
    if (chapterIdsList.length === 0) return ossMap;
    try {
      const { data } = await api.post('/oss-mp3/chapters/check', {
        chapter_ids: chapterIdsList,
        prefix: 'audios',
      });
      const items = Array.isArray(data?.chapters) ? data.chapters : [];
      for (const item of items) {
        const cid = item.chapter_id;
        const existing = new Set(Array.isArray(item.found_files) ? item.found_files : []);
        ossMap.set(cid, { existing, hasRequired: Boolean(item.has_required_files) });
      }
    } catch {
      // ignore
    }
    return ossMap;
  }, []);

  /* ── run one volume ── */
  const runVolumeSteps = useCallback(
    async (volume) => {
      const chapters = Array.isArray(volume.chapters)
        ? [...volume.chapters].sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0))
        : [];

      if (chapters.length === 0) {
        addLog(`[卷 ${volume.id}] 没有章节，跳过`, 'error');
        return 'skipped';
      }

      const ids = chapters.map((c) => c.id);
      const volLabel = volume.novel?.title
        ? `${volume.novel.title} — ${volume.title}`
        : volume.title || `卷 ${volume.id}`;

      addLog(`────── 开始处理卷: ${volLabel}（${chapters.length} 章）──────`);

      /* ── Step 0: 预检查 ── */
      addLog(`[预检查] 正在获取章节最新状态…`);

      // 重新拉取最新数据
      let freshChapters = chapters;
      try {
        const { data } = await api.get('/volumes/', {
          params: { limit: 500, include_chapters: true },
        });
        const list = Array.isArray(data) ? data : [];
        const freshVol = list.find((v) => v.id === volume.id);
        if (freshVol?.chapters) {
          freshChapters = [...freshVol.chapters].sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0));
        }
      } catch {
        addLog('[预检查] 获取最新数据失败，使用缓存', 'error');
      }

      const freshIds = freshChapters.map((c) => c.id);

      // 检查翻译/笔记
      const needTranslateIds = freshChapters.filter((c) => !c.has_content_trans).map((c) => c.id);
      const needNotesIds = freshChapters.filter((c) => !c.has_ai_notes).map((c) => c.id);

      addLog(`[预检查] 翻译：${freshIds.length - needTranslateIds.length}/${freshIds.length} 已完成，待处理 ${needTranslateIds.length} 章`);
      addLog(`[预检查] 笔记：${freshIds.length - needNotesIds.length}/${freshIds.length} 已完成，待处理 ${needNotesIds.length} 章`);

      // 检查 OSS
      let ossMap;
      try {
        ossMap = await fetchOssStatus(freshIds);
      } catch {
        ossMap = new Map();
      }

      const needTtsChapters = [];
      const needTtsModes = new Map();
      for (const ch of freshChapters) {
        const oss = ossMap.get(ch.id);
        const modes = [];
        if (!oss || !oss.existing.has('en.mp3')) modes.push('en');
        if (!oss || !oss.existing.has('cn.mp3')) modes.push('cn');
        if (modes.length > 0) {
          needTtsChapters.push(ch);
          needTtsModes.set(ch.id, modes);
        }
      }
      addLog(`[预检查] 语音：${freshIds.length - needTtsChapters.length}/${freshIds.length} 已完成，待处理 ${needTtsChapters.length} 章`);

      const needCombineChapters = freshChapters.filter((ch) => {
        const oss = ossMap.get(ch.id);
        if (!oss?.hasRequired) return false;
        if (oss.existing.has('en_cn.mp3') && oss.existing.has('cn_en.mp3')) return false;
        return true;
      });
      const alreadyCombined = freshChapters.filter((ch) => {
        const oss = ossMap.get(ch.id);
        return oss?.existing?.has('en_cn.mp3') && oss?.existing?.has('cn_en.mp3');
      }).length;
      addLog(`[预检查] 混音：${alreadyCombined}/${freshIds.length} 已完成，待处理 ${needCombineChapters.length} 章`);

      /* ── 定义步骤 ── */
      const pipelineSteps = [
        {
          key: 'translate',
          fn: needTranslateIds.length > 0
            ? async () => {
                updateStep('translate', { status: 'running', progress: 0, detail: '正在启动翻译任务…' });
                addLog(`[翻译] 开始翻译 ${needTranslateIds.length} 个章节`);
                const payload = {
                  chapter_ids: needTranslateIds,
                  target_language: 'English',
                  model: TRANSLATE_MODEL,
                  chunk_size: TRANSLATE_CHUNK_SIZE,
                  max_concurrent_requests: TRANSLATE_CONCURRENCY,
                };
                const { data } = await api.post('/openai-translate/batch', payload, { timeout: 60000 });
                if (!data?.task_id) throw new Error('未能启动翻译任务');
                const taskId = data.task_id;
                addLog(`[翻译] 任务 ${taskId.slice(0, 8)} 已启动，轮询中…`);
                let consecutiveErrors = 0;
                while (true) {
                  if (cancelledRef.current) throw new Error('用户取消');
                  await sleep(POLL_MS);
                  try {
                    const { data: s } = await api.get(`/openai-translate/batch/status/${taskId}`, { timeout: 30000 });
                    consecutiveErrors = 0;
                    const pct = s.progress || 0;
                    const detail = `${s.translated_sentences ?? 0}/${s.total_sentences ?? '?'} 句 — ${s.current_chapter_title || ''}`;
                    updateStep('translate', { progress: pct, detail });
                    if (s.status === 'completed') {
                      addLog(`[翻译] 完成，成功 ${s.succeeded_chapters}/${s.total_chapters} 章`, 'success');
                      updateStep('translate', { status: 'done', progress: 100, detail: `${s.succeeded_chapters}/${s.total_chapters} 章完成` });
                      return;
                    }
                    if (s.status === 'failed') throw new Error(s.error || '翻译失败');
                  } catch (pollErr) {
                    if (pollErr.message && !pollErr.response) throw pollErr;
                    consecutiveErrors += 1;
                    addLog(`[翻译] 轮询出错 (${consecutiveErrors}/5)`, 'error');
                    if (consecutiveErrors >= 5) throw new Error('轮询连续失败 5 次');
                  }
                }
              }
            : null,
          skipReason: needTranslateIds.length === 0 ? `全部 ${freshIds.length} 章已翻译` : null,
        },
        {
          key: 'notes',
          fn: needNotesIds.length > 0
            ? async () => {
                updateStep('notes', { status: 'running', progress: 0, detail: '正在批量生成笔记…' });
                addLog(`[笔记] 开始为 ${needNotesIds.length} 个章节生成 AI 笔记`);
                const { data } = await api.post('/ai-notes/batch', { chapter_ids: needNotesIds, model: NOTES_MODEL }, { timeout: 0 });
                const succeeded = data.succeeded ?? 0;
                const total = data.total_requested ?? needNotesIds.length;
                addLog(`[笔记] 完成，成功 ${succeeded}/${total} 章`, succeeded === total ? 'success' : 'error');
                updateStep('notes', { status: succeeded > 0 ? 'done' : 'failed', progress: 100, detail: `${succeeded}/${total} 章完成` });
                if (succeeded === 0) throw new Error('所有章节笔记生成失败');
              }
            : null,
          skipReason: needNotesIds.length === 0 ? `全部 ${freshIds.length} 章已有笔记` : null,
        },
        {
          key: 'tts',
          fn: needTtsChapters.length > 0
            ? async () => {
                const voiceMap = { en: selectedEnVoice.shortName, cn: selectedCnVoice.shortName };
                let totalTasks = 0;
                for (const ch of needTtsChapters) totalTasks += (needTtsModes.get(ch.id) || ['en', 'cn']).length;
                updateStep('tts', { status: 'running', progress: 0, detail: '准备中…' });
                addLog(`[语音] 开始处理 ${needTtsChapters.length} 个章节，共 ${totalTasks} 个任务`);
                let finished = 0;
                for (const chapter of needTtsChapters) {
                  if (cancelledRef.current) throw new Error('用户取消');
                  const modes = needTtsModes.get(chapter.id) || ['en', 'cn'];
                  for (const mode of modes) {
                    if (cancelledRef.current) throw new Error('用户取消');
                    const voice = voiceMap[mode];
                    const label = `章节 ${chapter.id} (${chapter.title || ''}) ${mode === 'en' ? '英文' : '中文'} → ${voice}`;
                    updateStep('tts', { detail: label, progress: Math.round((finished / totalTasks) * 100) });
                    try {
                      const { data } = await api.post(`${EDGE_TTS_PREFIX}/tts/chapter`, { chapter_id: chapter.id, mode, voice });
                      if (data?.task_id) await pollTtsTask(data.task_id);
                      addLog(`[语音] ${label} ✓`, 'success');
                    } catch (err) {
                      const msg = err?.response?.data?.detail || err.message || '';
                      addLog(`[语音] ${label} 失败：${msg}`, 'error');
                    }
                    finished += 1;
                  }
                }
                updateStep('tts', { status: 'done', progress: 100, detail: `${needTtsChapters.length} 章完成` });
                addLog('[语音] 全部完成', 'success');
              }
            : null,
          skipReason: needTtsChapters.length === 0 ? `全部 ${freshIds.length} 章音频已生成` : null,
        },
        {
          key: 'combine',
          fn: needCombineChapters.length > 0
            ? async () => {
                updateStep('combine', { status: 'running', progress: 0, detail: '准备中…' });
                addLog(`[混音] 开始为 ${needCombineChapters.length} 个章节混合音频`);
                let finished = 0;
                let failCount = 0;
                for (const chapter of needCombineChapters) {
                  if (cancelledRef.current) throw new Error('用户取消');
                  const label = `章节 ${chapter.id} (${chapter.title || ''})`;
                  updateStep('combine', { detail: label, progress: Math.round((finished / needCombineChapters.length) * 100) });
                  try {
                    await api.post('/oss-mp3/combine', { chapter_id: chapter.id, oss_prefix: `audios/${chapter.id}`, overwrite: false });
                    addLog(`[混音] ${label} ✓`, 'success');
                  } catch (err) {
                    addLog(`[混音] ${label} 失败：${err?.response?.data?.detail || err.message || ''}`, 'error');
                    failCount += 1;
                  }
                  finished += 1;
                }
                const successCount = needCombineChapters.length - failCount;
                updateStep('combine', { status: failCount === needCombineChapters.length ? 'failed' : 'done', progress: 100, detail: `${successCount}/${needCombineChapters.length} 章完成` });
                addLog(`[混音] 全部完成，成功 ${successCount}/${needCombineChapters.length}`, failCount === 0 ? 'success' : 'error');
              }
            : null,
          skipReason: needCombineChapters.length === 0 ? '全部混音已完成' : null,
        },
      ];

      /* ── 执行 ── */
      let hasFailed = false;
      for (const step of pipelineSteps) {
        if (cancelledRef.current) {
          updateStep(step.key, { status: 'skipped', detail: '已取消' });
          continue;
        }
        if (!step.fn) {
          updateStep(step.key, { status: 'done', progress: 100, detail: step.skipReason });
          addLog(`[${STEP_LABELS[step.key]}] 跳过 — ${step.skipReason}`, 'success');
          continue;
        }
        try {
          await step.fn();
        } catch (err) {
          const msg = err.message || '未知错误';
          updateStep(step.key, { status: 'failed', detail: msg });
          addLog(`[${STEP_LABELS[step.key]}] 失败: ${msg}`, 'error');
          hasFailed = true;
        }
      }

      addLog(`────── 卷 ${volLabel} 处理结束 ──────`, hasFailed ? 'error' : 'success');
      return hasFailed ? 'failed' : 'done';
    },
    [addLog, updateStep, fetchOssStatus, pollTtsTask, selectedEnVoice, selectedCnVoice],
  );

  /* ────────── 全部卷流水线 ────────── */
  const startAllPipeline = useCallback(async () => {
    if (!selectedEnVoice || !selectedCnVoice) {
      setGlobalError('请先选择英文和中文语音音色');
      return;
    }
    if (selectedEnVoice.category === 'zh') {
      setGlobalError(`英文音色选择了中文声音 (${selectedEnVoice.shortName})，请重新选择`);
      return;
    }
    if (selectedCnVoice.category === 'en') {
      setGlobalError(`中文音色选择了英文声音 (${selectedCnVoice.shortName})，请重新选择`);
      return;
    }
    if (selectedVolumes.length === 0) {
      setGlobalError('请至少选择一个卷');
      return;
    }

    setGlobalError('');
    setLogs([]);
    setVolumeResults(new Map());
    setRunning(true);
    cancelledRef.current = false;

    addLog(`===== 全量流水线启动 =====`);
    addLog(`共 ${selectedVolumes.length} 个卷，${totalChapters} 个章节`);
    addLog(`英文音色: ${selectedEnVoice.shortName} | 中文音色: ${selectedCnVoice.shortName}`);

    for (let i = 0; i < selectedVolumes.length; i++) {
      if (cancelledRef.current) {
        addLog('用户取消，跳过后续卷', 'error');
        // 标记剩余卷为 skipped
        setVolumeResults((prev) => {
          const next = new Map(prev);
          for (let j = i; j < selectedVolumes.length; j++) {
            next.set(selectedVolumes[j].id, 'skipped');
          }
          return next;
        });
        break;
      }

      const vol = selectedVolumes[i];
      setCurrentVolIdx(i);
      setCurrentVolId(vol.id);
      resetSteps();

      addLog(`\n========== [${i + 1}/${selectedVolumes.length}] 卷: ${vol.novel?.title || ''} — ${vol.title || `#${vol.id}`} ==========`);

      let result;
      try {
        result = await runVolumeSteps(vol);
      } catch (err) {
        result = 'failed';
        addLog(`卷 ${vol.id} 异常: ${err.message}`, 'error');
      }

      setVolumeResults((prev) => {
        const next = new Map(prev);
        next.set(vol.id, result);
        return next;
      });
    }

    setCurrentVolIdx(-1);
    setCurrentVolId(null);
    setRunning(false);
    addLog(`\n===== 全量流水线结束 =====`, 'success');
  }, [selectedEnVoice, selectedCnVoice, selectedVolumes, totalChapters, addLog, resetSteps, runVolumeSteps]);

  const handleStop = useCallback(() => {
    cancelledRef.current = true;
    addLog('用户请求停止…', 'error');
  }, [addLog]);

  /* ── voice label ── */
  const renderVoiceLabel = (voice) => {
    const name = voice.localName || voice.displayName || voice.shortName;
    const parts = [name];
    if (voice.gender) {
      parts.push(`[${voice.gender === 'Female' ? '女' : voice.gender === 'Male' ? '男' : voice.gender}]`);
    }
    parts.push(`| ${voice.locale}`);
    if (voice.category === 'multilingual') parts.push('| 多语言');
    return parts.join(' ');
  };

  /* ── render step dot ── */
  const dotClass = (status) => {
    switch (status) {
      case 'running': return styles.stepDotRunning;
      case 'done': return styles.stepDotDone;
      case 'failed': return styles.stepDotFailed;
      case 'skipped': return styles.stepDotSkipped;
      default: return styles.stepDotIdle;
    }
  };

  const dotIcon = (status, idx) => {
    switch (status) {
      case 'done': return '✓';
      case 'failed': return '✕';
      case 'skipped': return '—';
      default: return idx + 1;
    }
  };

  const statusText = (status) => {
    switch (status) {
      case 'idle': return '等待';
      case 'running': return '进行中';
      case 'done': return '完成';
      case 'failed': return '失败';
      case 'skipped': return '跳过';
      default: return '';
    }
  };

  const statusClass = (status) => {
    switch (status) {
      case 'running': return styles.stepStatusRunning;
      case 'done': return styles.stepStatusDone;
      case 'failed': return styles.stepStatusFailed;
      default: return '';
    }
  };

  /* ── volume status badge ── */
  const volStatusBadge = (volId) => {
    const r = volumeResults.get(volId);
    if (!r) {
      if (currentVolId === volId) return <span className={styles.volBadgeRunning}>处理中…</span>;
      return null;
    }
    switch (r) {
      case 'done': return <span className={styles.volBadgeDone}>✓ 完成</span>;
      case 'failed': return <span className={styles.volBadgeFailed}>✕ 部分失败</span>;
      case 'skipped': return <span className={styles.volBadgeSkipped}>— 跳过</span>;
      default: return null;
    }
  };

  /* ── overall progress ── */
  const doneCount = [...volumeResults.values()].filter((v) => v === 'done').length;
  const failedCount = [...volumeResults.values()].filter((v) => v === 'failed').length;

  /* ────────── render ────────── */
  return (
    <div className={styles.page}>
      {/* ── 语音音色配置 ── */}
      <div className={styles.configCard}>
        <h2 className={styles.configTitle}>全量一键补全</h2>
        <p className={styles.configHint}>
          列出所有卷，拖拽排序后，按顺序逐卷执行：翻译 → AI 笔记 → 语音合成 → 混音。已完成的步骤会自动跳过。
        </p>

        {/* English Voice */}
        <div className={styles.fieldGroup}>
          <label className={styles.label} htmlFor="allEnVoice">
            英文音色
            {selectedEnVoice && <span className={styles.labelHint}>{selectedEnVoice.shortName}</span>}
          </label>
          <div className={styles.voiceFilterRow}>
            {VOICE_CATEGORIES.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={`${styles.filterChip} ${enVoiceFilter === opt.value ? styles.filterChipActive : ''}`}
                onClick={() => {
                  setEnVoiceFilter(opt.value);
                  const next = opt.value ? voices.filter((v) => v.category === opt.value) : voices;
                  if (selectedEnVoice && !next.some((v) => v.shortName === selectedEnVoice.shortName)) {
                    setSelectedEnVoice(next.length > 0 ? next[0] : null);
                  }
                }}
                disabled={running}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <select
            id="allEnVoice"
            className={styles.select}
            value={selectedEnVoice?.shortName || ''}
            onChange={(e) => setSelectedEnVoice(enFilteredVoices.find((x) => x.shortName === e.target.value) || null)}
            disabled={voicesLoading || enFilteredVoices.length === 0 || running}
          >
            {enFilteredVoices.length === 0 && <option value="">暂无声音</option>}
            {enFilteredVoices.map((v) => (
              <option key={v.shortName} value={v.shortName}>{renderVoiceLabel(v)}</option>
            ))}
          </select>
        </div>

        {/* Chinese Voice */}
        <div className={styles.fieldGroup}>
          <label className={styles.label} htmlFor="allCnVoice">
            中文音色
            {selectedCnVoice && <span className={styles.labelHint}>{selectedCnVoice.shortName}</span>}
          </label>
          <div className={styles.voiceFilterRow}>
            {VOICE_CATEGORIES.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={`${styles.filterChip} ${cnVoiceFilter === opt.value ? styles.filterChipActive : ''}`}
                onClick={() => {
                  setCnVoiceFilter(opt.value);
                  const next = opt.value ? voices.filter((v) => v.category === opt.value) : voices;
                  if (selectedCnVoice && !next.some((v) => v.shortName === selectedCnVoice.shortName)) {
                    setSelectedCnVoice(next.length > 0 ? next[0] : null);
                  }
                }}
                disabled={running}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <select
            id="allCnVoice"
            className={styles.select}
            value={selectedCnVoice?.shortName || ''}
            onChange={(e) => setSelectedCnVoice(cnFilteredVoices.find((x) => x.shortName === e.target.value) || null)}
            disabled={voicesLoading || cnFilteredVoices.length === 0 || running}
          >
            {cnFilteredVoices.length === 0 && <option value="">暂无声音</option>}
            {cnFilteredVoices.map((v) => (
              <option key={v.shortName} value={v.shortName}>{renderVoiceLabel(v)}</option>
            ))}
          </select>
        </div>

        {globalError && <div className={styles.errorMessage}>{globalError}</div>}

        <div className={styles.actionRow}>
          {!running ? (
            <button
              type="button"
              className={styles.startBtn}
              disabled={volumesLoading || voicesLoading || selectedVolumes.length === 0 || !selectedEnVoice || !selectedCnVoice}
              onClick={startAllPipeline}
            >
              开始处理 ({selectedVolumes.length} 卷 / {totalChapters} 章)
            </button>
          ) : (
            <button type="button" className={styles.stopBtn} onClick={handleStop}>
              停止
            </button>
          )}
        </div>
      </div>

      {/* ── 卷列表（可排序） ── */}
      <div className={styles.volumeListCard}>
        <div className={styles.volumeListHeader}>
          <h3 className={styles.volumeListTitle}>
            卷列表
            <span className={styles.volumeListCount}>
              {volumesLoading ? '加载中…' : `共 ${orderedVolumes.length} 个`}
            </span>
          </h3>
          <div className={styles.volumeListActions}>
            <button
              type="button"
              className={styles.selectAllBtn}
              onClick={toggleSelectAll}
              disabled={running}
            >
              {selectedIds.size === orderedVolumeIds.length ? '取消全选' : '全选'}
            </button>
          </div>
        </div>

        {/* overall progress */}
        {running && (
          <div className={styles.overallProgress}>
            <div className={styles.overallProgressBar}>
              <div
                className={styles.overallProgressFill}
                style={{ width: `${selectedVolumes.length > 0 ? Math.round(((currentVolIdx + 1) / selectedVolumes.length) * 100) : 0}%` }}
              />
            </div>
            <span className={styles.overallProgressText}>
              {currentVolIdx + 1}/{selectedVolumes.length} 卷 | 完成: {doneCount} | 失败: {failedCount}
            </span>
          </div>
        )}

        <div className={styles.volumeList}>
          {orderedVolumes.map((vol, idx) => {
            const chapCount = vol.chapters?.length || 0;
            const isSelected = selectedIds.has(vol.id);
            const isCurrent = currentVolId === vol.id;
            const novelTitle = vol.novel?.title || '';

            return (
              <div
                key={vol.id}
                className={`${styles.volumeItem} ${isCurrent ? styles.volumeItemCurrent : ''} ${!isSelected ? styles.volumeItemDisabled : ''}`}
                draggable={!running}
                onDragStart={() => handleDragStart(idx)}
                onDragEnter={() => handleDragEnter(idx)}
                onDragEnd={handleDragEnd}
                onDragOver={(e) => e.preventDefault()}
              >
                {/* 拖拽手柄 */}
                <div className={styles.dragHandle} title="拖拽排序">
                  <span className={styles.dragIcon}>⠿</span>
                </div>

                {/* 勾选 */}
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleSelect(vol.id)}
                    disabled={running}
                    className={styles.checkbox}
                  />
                </label>

                {/* 序号 */}
                <span className={styles.volumeIdx}>{idx + 1}</span>

                {/* 卷信息 */}
                <div className={styles.volumeInfo}>
                  <span className={styles.volumeTitle}>
                    {novelTitle ? `${novelTitle} — ` : ''}{vol.title || `卷 ${vol.id}`}
                  </span>
                  <span className={styles.volumeMeta}>
                    {chapCount} 章
                  </span>
                </div>

                {/* 状态 */}
                <div className={styles.volumeStatus}>
                  {volStatusBadge(vol.id)}
                </div>

                {/* 上下移动按钮 */}
                <div className={styles.moveButtons}>
                  <button
                    type="button"
                    className={styles.moveBtn}
                    onClick={() => idx > 0 && moveVolume(idx, idx - 1)}
                    disabled={running || idx === 0}
                    title="上移"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className={styles.moveBtn}
                    onClick={() => idx < orderedVolumeIds.length - 1 && moveVolume(idx, idx + 1)}
                    disabled={running || idx === orderedVolumeIds.length - 1}
                    title="下移"
                  >
                    ↓
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── 当前卷步骤进度 ── */}
      {(running || currentVolIdx >= 0) && (
        <div className={styles.stepsCard}>
          <h3 className={styles.stepsTitle}>
            {currentVolId
              ? `当前卷进度 — ${volumeMap.get(currentVolId)?.title || `#${currentVolId}`}`
              : '流水线进度'}
          </h3>
          {STEP_KEYS.map((key, idx) => {
            const st = steps[key];
            return (
              <div key={key} className={styles.stepItem}>
                <div className={styles.stepIndicatorCol}>
                  <div className={`${styles.stepDot} ${dotClass(st.status)}`}>
                    {dotIcon(st.status, idx)}
                  </div>
                  {idx < STEP_KEYS.length - 1 && (
                    <div className={`${styles.stepLine} ${st.status === 'done' ? styles.stepLineDone : ''}`} />
                  )}
                </div>
                <div className={styles.stepContent}>
                  <div className={styles.stepHeader}>
                    <span className={styles.stepName}>{STEP_LABELS[key]}</span>
                    <span className={`${styles.stepStatusText} ${statusClass(st.status)}`}>
                      {statusText(st.status)}
                    </span>
                  </div>
                  {st.detail && <div className={styles.stepDetail}>{st.detail}</div>}
                  {(st.status === 'running' || st.status === 'done') && (
                    <div className={styles.progressBarWrapper}>
                      <div
                        className={`${styles.progressBarFill} ${st.status === 'done' ? styles.progressBarFillDone : ''}`}
                        style={{ width: `${st.progress}%` }}
                      />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Logs ── */}
      <div className={styles.logCard}>
        <div className={styles.logHeader}>
          <h3 className={styles.logTitle}>日志</h3>
          {logs.length > 0 && (
            <button type="button" className={styles.logClearBtn} onClick={() => setLogs([])}>
              清空
            </button>
          )}
        </div>
        <div className={styles.logBody}>
          {logs.length === 0 && <div style={{ color: 'var(--gray-40)' }}>暂无日志</div>}
          {logs.map((entry, i) => (
            <div key={i} className={styles.logEntry}>
              <span className={styles.logTime}>{entry.time}</span>
              <span
                className={`${styles.logText} ${
                  entry.type === 'error' ? styles.logError : entry.type === 'success' ? styles.logSuccess : ''
                }`}
              >
                {entry.text}
              </span>
            </div>
          ))}
          <div ref={logEndRef} />
        </div>
      </div>
    </div>
  );
}

export default BatchAllVolumesTool;

